import { QuestionsForManagerQueryOptions, getSurveyOptions } from "@pulse/api/queryApi";
import { createQuestion, type CreateQuestion } from "@pulse/api/surveyApi";
import { useCreateSurvey } from "@pulse/hooks/surveyHooks";
import usePulseId from "@pulse/hooks/usePulseId";
import { queryClient } from "@pulse/queryClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import ActionButton from "./ActionButton";
import { useSelectedCustomerIdParam } from "@/features/hub/selectedCustomerContext";
import {
    Button,
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "frontend-components";
import { faCopy } from "@fortawesome/pro-regular-svg-icons";

export default function CopySurvey({ surveyId }: { surveyId: number }) {
    const { data: questions } = useQuery(QuestionsForManagerQueryOptions({ surveyId }));
    const { t } = useTranslation();
    const navigate = useNavigate();
    const pulseId = usePulseId();
    const customerId = useSelectedCustomerIdParam();
    const { data: survey } = useQuery(getSurveyOptions({ surveyId }));
    const createSurveyMutation = useCreateSurvey({
        pulseInstance: Number(pulseId)!,
    });

    if (!survey) return null;

    const handleCopy = async () => {
        const { id: newSurveyId, pulseInstanceId } = await createSurveyMutation.mutateAsync({
            title: survey.title,
            description: survey.description,
        });
        if (questions) {
            await Promise.all(
                questions.map(async (question) => {
                    await createQuestion({
                        surveyId: newSurveyId,
                        data: {
                            questionText: question.questionText,
                            questionOrder: question.questionOrder,
                            questionType: question.questionType,
                            questionOptions: question.questionOptions,
                            allowComments: question.allowComments,
                            optional: question.optional,
                            driverId: question.driverId,
                        } as CreateQuestion,
                    });
                }),
            );
        }
        await queryClient.refetchQueries(
            QuestionsForManagerQueryOptions({ surveyId: newSurveyId }),
        );
        navigate({
            to: "/$customerId/pulse/$pulseId/surveys/$surveyId/edit",
            params: {
                customerId,
                surveyId: String(newSurveyId),
                pulseId: String(pulseInstanceId),
            },
        });
    };

    return (
        <Dialog>
            <DialogTrigger
                render={
                    <ActionButton label={t("responseOverview.labelCopy")}>
                        <FontAwesomeIcon icon={faCopy} size="xl" className="scale-90 -mx-1" />
                    </ActionButton>
                }
            />
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t("responseOverview.copySurveyTitle")}</DialogTitle>
                    <DialogDescription>
                        {t("responseOverview.copySurveyDescription")}
                    </DialogDescription>
                </DialogHeader>

                <div className="rounded-[3px] border border-compendia-border bg-surface-subtle p-3">
                    <p className="text-xs text-text-secondary">
                        {t("responseOverview.copySurveyNewNameLabel")}
                    </p>
                    <p className="text-compendia-foreground">{survey.title}</p>
                </div>

                {createSurveyMutation.isError && (
                    <p className="text-center text-error">
                        {t("responseOverview.copySurveyError")}
                    </p>
                )}

                <DialogFooter>
                    <DialogClose
                        render={
                            <Button variant="outline" disabled={createSurveyMutation.isPending}>
                                {t("common.cancel")}
                            </Button>
                        }
                    />
                    <Button
                        variant="fill"
                        onClick={handleCopy}
                        disabled={createSurveyMutation.isPending}
                    >
                        {t("responseOverview.copySurveyConfirm")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
